import upperFirst from 'lodash/upperFirst'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { EmailCampaign } from 'types/campaign'
import {
  campaignListBeginCreating,
  updateCampaignSearchQuery,
} from '@disco/state-management/actions/campaign-list-actions'
import type { CampaignStatus } from '@disco/constants/CampaignTypes'
import {
  STATUS_DRAFT,
  STATUS_SCHEDULED,
  STATUS_SENT,
} from '@disco/constants/CampaignTypes'
import { EMAIL_CAMPAIGN_BUILDER } from '@disco/constants/FeatureTypes'
import {
  useFeatureData,
  useFeatureEnabled,
} from '@disco/state-management/selectors/business'
import {
  getCampaignListSortedFiltered,
  getCampaignSearchQuery,
} from '@disco/state-management/selectors/campaigns'
import Alert from 'components/ui/alert'
import Breadcrumbs from 'components/ui/breadcrumbs'
import Button from 'components/ui/button'
import ContactUsLink from 'components/ui/contact-us-link'
import Flex from 'components/ui/flex'
import Icon from 'components/ui/icon'
import PageContainer from 'components/ui/page-container'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'
import CampaignHeader from 'pages/campaigns/campaign-header'
import CampaignIntroduction from 'pages/campaigns/campaign-introduction'
import CampaignNoResultsSearch from 'pages/campaigns/campaign-no-results-search'
import CampaignTable from 'pages/campaigns/campaign-table'
import styles from './index.module.css'

const CAMPAIGN_PREVIEW_LIMIT = 5
type Props = {
  fullPage?: CampaignStatus
}

const CampaignListPage = ({ fullPage }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const campaigns: {
    loading: boolean
    loaded: boolean
    emailCampaigns: Array<EmailCampaign>
  } = useSelector(state => getCampaignListSortedFiltered(state, fullPage))
  const { isFeatureEnabled, isFeatureSuspended, isFeatureUpgradeAvailable } =
    useFeatureData(EMAIL_CAMPAIGN_BUILDER)
  const campaignSearchQuery = useSelector(getCampaignSearchQuery)
  const empty = campaigns.emailCampaigns.length === 0
  const showCampaign = isFeatureEnabled || !empty || campaignSearchQuery
  const showNoResults = campaignSearchQuery && empty
  const showGetStarted = isFeatureEnabled && empty && !campaignSearchQuery
  const onSearch = useCallback(({ target }) => {
    dispatch(updateCampaignSearchQuery(target.value))
  }, [])
  let tables = null

  if (fullPage) {
    tables = (
      <CampaignTable isFeatureEnabled={isFeatureEnabled} status={fullPage} />
    )
  } else {
    tables = (
      <Stack space="large">
        <CampaignTable
          isFeatureEnabled={isFeatureEnabled}
          status={STATUS_DRAFT}
          limit={CAMPAIGN_PREVIEW_LIMIT}
        />
        <CampaignTable
          isFeatureEnabled={isFeatureEnabled}
          status={STATUS_SCHEDULED}
          limit={CAMPAIGN_PREVIEW_LIMIT}
        />
        <CampaignTable
          isFeatureEnabled={isFeatureEnabled}
          status={STATUS_SENT}
          limit={CAMPAIGN_PREVIEW_LIMIT}
        />
      </Stack>
    )
  }

  return showCampaign ? (
    <div className="Page_Content">
      <Stack space="xsmall">
        {fullPage ? (
          <Breadcrumbs
            breadcrumbs={[
              {
                content: 'Email Creator',
                link: '/campaigns/list',
              },
              {
                content: upperFirst(fullPage),
              },
            ]}
          />
        ) : null}
        <CampaignHeader
          customPageTitle={upperFirst(fullPage)}
          nextButton={
            <Flex>
              <TextField
                value={campaignSearchQuery}
                placeholder="Search name"
                icon={
                  <Icon
                    type="search"
                    width="24"
                    color="#8d97a1"
                    className={styles.campaignListPageSearchIcon}
                  />
                }
                onChange={onSearch}
                nodeRight={
                  <Button
                    compact
                    appearance="icon"
                    icon="clear"
                    disabled={!campaignSearchQuery}
                    onClick={() => dispatch(updateCampaignSearchQuery(''))}
                  />
                }
              />
              <Button
                disabled={!isFeatureEnabled}
                appearance="primary"
                caption="New email"
                onClick={() => dispatch(campaignListBeginCreating('campaign'))}
              />
            </Flex>
          }
        />
      </Stack>

      {isFeatureUpgradeAvailable && (
        <div className={styles.campaignListPageAlert}>
          <Alert>
            The Email Creator is available on Plus and Pro plans.{' '}
            <ContactUsLink>Upgrade to access</ContactUsLink>
          </Alert>
        </div>
      )}
      {isFeatureSuspended && (
        <div className={styles.campaignListPageAlert}>
          <Alert status="danger" icon="warning">
            Your Email creator access has been suspended until further notice.
            We’ve sent you an email with more information.
          </Alert>
        </div>
      )}

      <PageContainer>
        {showGetStarted && (
          <CampaignIntroduction
            getStarted={() => dispatch(campaignListBeginCreating('campaign'))}
          />
        )}
        {showNoResults && <CampaignNoResultsSearch />}
        {!showGetStarted && !showNoResults && tables}
      </PageContainer>
    </div>
  ) : (
    <CampaignIntroduction />
  )
}

export default CampaignListPage
